import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { shortDate, today } from '../../lib/dates';
import { thumbnailUrl } from '../../lib/youtube';
import { KIND, byDue, goesOutOn, isOpen, isOut, requirement } from '../../lib/kinds';
import type { Assignment } from '../../lib/types';
import { useClassroom } from '../../state/Classroom';

export default function Assignments() {
  const { hydrated, assignments, drafts, students, progressFor, dueFor } = useClassroom();
  if (!hydrated) return <Loading />;
  const t = today();
  // By due date, not by week: what's open now, what's scheduled to go out, and what's past due.
  const open = assignments.filter(a => isOpen(a, t, students.map(s => dueFor(a, s.user_id)))).sort(byDue);
  const scheduled = assignments.filter(a => !isOut(a, t)).sort((a, b) => goesOutOn(a).localeCompare(goesOutOn(b)));
  const past = assignments.filter(a => isOut(a, t) && !open.includes(a)).sort((a, b) => -byDue(a, b));
  const sections: [string, Assignment[], string?][] = [
    ['Open now', open, 'pill'], ['Scheduled', scheduled, 'pill muted'], ['Past due', past],
  ];

  return (
    <div>
      <div className="row">
        <h1>Assignments</h1>
        <span className="spacer" />
        <Link className="button" to="/assign?kind=video">+ Video</Link>
        <Link className="button" to="/assign?kind=story">+ Story</Link>
        <Link className="button" to="/assign?kind=grammar">+ Grammar</Link>
        <Link className="button" to="/library">📚 Library</Link>
      </div>
      <p className="sub">Students see an assignment on the day it goes out. Drafts stay hidden until you assign them.</p>

      {drafts.length > 0 && (
        <section>
          <h2>Drafts <span className="pill muted">Not assigned</span></h2>
          <div className="list">
            {drafts.map(a => (
              <div key={a.id} className="item">
                <Thumb a={a} />
                <span className="grow">
                  <Link to={`/a/${a.id}`} className="term">{a.title}</Link>
                  <span className="gloss small">{KIND[a.kind].label} · {requirement(a, true)} · saved {shortDate(a.created_at)}</span>
                </span>
                <Link to={`/assign/${a.id}`} className="button small">Edit &amp; assign</Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {assignments.length === 0 && <div className="notice">Nothing assigned yet. Start with a video or a story.</div>}

      {sections.filter(([, list]) => list.length > 0).map(([name, list]) => (
        <section key={name}>
          <h2>{name} <span className="pill muted">{list.length}</span></h2>
          <div className="list">
            {list.map(a => {
              const done = students.filter(s => progressFor(a.id, s.user_id)?.completed_at).length;
              return (
                <div key={a.id} className="item">
                  <Thumb a={a} />
                  <span className="grow">
                    <Link to={`/a/${a.id}`} className="term">{a.title}</Link>
                    <span className="gloss small">
                      {KIND[a.kind].label} · {requirement(a, true)}
                      {!isOut(a, t) && ` · goes out ${shortDate(goesOutOn(a))}`}
                      {a.due_date && ` · due ${shortDate(a.due_date)}`}
                      {isOut(a, t) && ` · ${done}/${students.length} done`}
                    </span>
                  </span>
                  <Link to={`/assign/${a.id}`} className="button small">Edit</Link>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function Thumb({ a }: { a: Assignment }) {
  return a.kind === 'video'
    ? <img src={thumbnailUrl(a.video_id!)} alt="" className="thumb" />
    : <span className="thumb story-thumb">{KIND[a.kind].icon}</span>;
}
